/*
 *  			'License for ONE AXA SOFTWARE - Inner Source'
 * 					L O A S - I S
 *
 * The following License aims to be applied between the AXA Companies, according to the following definition:
 * "AXA Companies" means AXA, Société Anonyme with a “AXA Companies ” Board of Directors having its principal offices at 25, avenue Matignon, 75008 Paris, registered on the Commercial Registry of Paris under the number 572 093 920; and (i) any other company controlled by, or controlling AXA, with a company being considered as controlling another: (a) when it holds directly or indirectly a portion of the capital according to it the majority of the voting rights in general meetings of shareholders of this company; (b) when it holds solely the majority of the voting rights in this company by virtue of an agreement concluded with other partners or shareholders and which is not contrary to the interest of the company; (c) when it determines de facto, by voting rights which it holds, the decisions in the general meetings of shareholders of this company; (d) in any event, when it holds, directly or indirectly, a portion of voting rights greater than 40% and when no other partner or shareholder holds directly or indirectly a portion which is greater than its own; (ii) any economic interest group in which AXA and/or one or more other Companies of the AXA Group participates for at least 50% in operating costs; (iii) in the cases where the law applicable to a company limits voting rights or control (such as defined here in above), this company will be deemed to be a company of the AXA Group, if the voting rights in 2 Binding Corporate Rules – AXA Group – version 2013 general shareholders’ meetings or the control held by a Company of the AXA Group reaches the maximum amount fixed by said applicable law; and (iv) all AXA Companies constitute the “AXA Group” (hereafter "One AXA").  (Binding Corporate Rules - AXA Group - version 2019).
 *
 * Software credits:
 * 	Creator: AXA GO GETD
 * 	Main contributors: AXA GO GETD
 *
 * Permission is hereby granted, free of charge, to any person or entity within One AXA obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software within One AXA, including the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
 *
 * The above credit notice and this permission notice shall be included in all copies or substantial portions of the Software.
 * Any improvement or modification of the Software shall be made available to the whole One AXA, under the terms of this License.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

/* eslint-disable no-nested-ternary */
/* eslint-disable no-unused-vars */
/*
 * Copyright (c) 2020 AXA Group Operations Spain S.A.
 *
 * Licensed under the AXA Group Operations Spain S.A. License (the "License");
 * you may not use this file except in compliance with the License.
 * A copy of the License can be found in the LICENSE.TXT file distributed
 * together with this file.
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useState, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Table, Row, Col, Button, Form, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';

import { isUserAbleToSeeThis } from '@utils/authentication';
import { MESSAGES } from '@translations/labels';
import { useAgentId } from '@hooks/useAgentId';
import { usePaginationWrapper } from '@hooks/usePaginationWrapper';
import conversationActions from '@store/actions/conversations';
import { resetConversation } from '@store/reducers/conversations';
import { useQuery } from '@utils/locationHelper.js';
import { parseJson } from '@utils/toolsHelper.js';
import { getCustomNoData } from '@utils/noDataCustom';
import { FiltersArea } from '@components/commons/filtersArea/filtersArea';
import DSSwitch from '@design-system/switch/DSSwitch';
import domainActions from '@store/actions/domains';
import historyApi from '@api/history';
import { createExcel } from '@components/commons/excel/excel-utils';
import { AppContext } from '@commons/contexts/AppContext';
import { useTriggerReassignment } from '../../hooks/useTriggerReassignment';
import { useGetFilteredHistory } from '../../hooks/useGetFilteredHistory';
import { useGetActivity } from '../../hooks/useGetActivity';
import { showReassignModal } from '../reassign-modal/helper';
import { mapHistoryResponses } from './conversationHistoryHelper';
import byMessageTableStyles from './byMessageTableStyles';
import { generateColumns } from './data-columns';

const labels = { ...MESSAGES.conversationHistory };

const ByMessageTable = () => {
  const dispatch = useDispatch();

  const { getFilteredHistory } = useGetFilteredHistory();
  const { triggerActivitySearch } = useGetActivity();
  const { sendTriggerReassignment } = useTriggerReassignment();
  const { axaConfig } = useContext(AppContext);
  const { agentId } = useAgentId();
  const currentAgent = useSelector(state => state.agents?.current);
  const domainLanguages = useSelector(state => state.domains.languages);

  const q = useQuery().get('q');
  const analyticCase = useQuery().get('analyticCase');
  let defaultChecked = true;
  const urlQuery = parseJson(q);

  if ('isRemoteDataSource' in urlQuery) {
    defaultChecked = urlQuery.isRemoteDataSource;
  }
  if (urlQuery['nlp.intent']) {
    defaultChecked = true;
  }

  const buildQuery = (isRemoteDataSource, localAnalyticCase) => {
    const query = {
      ...urlQuery,
      isRemoteDataSource,
      analyticCase: localAnalyticCase,
    };
    return query;
  };

  const [isRemoteDataSource, setIsRemoteDataSource] = useState(defaultChecked);

  const reassignResult = payload => {
    const { newAssignment, prevAssignment } = payload;

    if (newAssignment !== prevAssignment) {
      sendTriggerReassignment(payload, isRemoteDataSource);
    }
  };

  const pageSize = 5;
  const defaultPagination = {
    limit: pageSize,
    offset: 0,
  };
  const defaultFilters = {
    state: [],
    language: [],
  };

  useEffect(() => {
    dispatch(resetConversation());
    dispatch(domainActions.getDomainLanguages(agentId));
    getFilteredHistory(buildQuery(defaultChecked, analyticCase), defaultPagination);
  }, []);

  const [currentFilters, setCurrentFilters] = useState(defaultFilters);
  const { paginationWrapper } = usePaginationWrapper();

  const getExcelColumns = agentName => [
    { header: 'Conversation id', supplier: row => row.conversationId },
    { header: 'CID', supplier: row => row.cid },
    { header: 'Message created at', supplier: row => row.createdAt },
    { header: 'Message id', supplier: row => row.id },
    { header: 'Who', supplier: row => row.who },
    { header: 'Locale', supplier: row => row.locale },
    { header: 'Intent name', supplier: row => row.intent },
    { header: 'Type', supplier: row => row.type },
    { header: 'Score', supplier: row => row.score },
    { header: 'Message', supplier: row => row.text },
    {
      header: 'Dialogs',
      supplier: row => {
        let output = '';
        if (row?.dialogs) {
          output = row.dialogs.reduce((acc, d) => acc + d.dialog + ', ', '[') + ']';
        }
        return output;
      },
    },
  ];

  const getCurrentProgress = conversationsSet => results => {
    const { items } = results;
    items.forEach(item => conversationsSet.add(item.conversationId));
    return conversationsSet.size;
  };

  const handleExport = () => {
    const conversationsSet = new Set();
    createExcel(
      (batchSize, offset) =>
        historyApi.getFullHistory(agentId, buildQuery(defaultChecked, analyticCase), {
          filters: currentFilters,
          limit: batchSize,
          offset,
        }),
      res => console.log('error retrieving datra for export'),
      res => res.data.items,
      res => res.data.total,
      getCurrentProgress(conversationsSet),
      `${new Date().getTime()}-${currentAgent.name}-${axaConfig.environment}-history-${agentId}.xlsx`,
      getExcelColumns(currentAgent.name)
    );
  };

  const handleReassignProcess = record => {
    dispatch(conversationActions.emptyActivity());
    triggerActivitySearch(record.agent, record.conversationId, record.id, isRemoteDataSource);
    showReassignModal(record, reassignResult);
  };

  const rawMessages = useSelector(state => state.conversations.all);
  const isLoadingMessages = useSelector(state => state.conversations.loading);
  const total = rawMessages ? (rawMessages.total ? rawMessages.total : rawMessages.length) : 0;
  const messages = rawMessages ? mapHistoryResponses(rawMessages.items ? rawMessages.items : rawMessages) : [];

  const toggleDataSource = () => {
    const isRemote = !isRemoteDataSource;
    setIsRemoteDataSource(isRemote);
    setCurrentFilters(defaultFilters);
    getFilteredHistory(buildQuery(isRemote, analyticCase), defaultPagination);
  };

  const onTableChange = (pagination, filters, sorter) => {
    Object.keys(filters).forEach(key => {
      if (!filters[key]) {
        filters[key] = [];
      }
    });

    setCurrentFilters({
      ...currentFilters,
      ...filters,
    });

    const paginationSettings = {
      limit: pagination.pageSize,
      offset: pagination.pageSize * (pagination.current - 1),
    };
    getFilteredHistory(buildQuery(isRemoteDataSource, analyticCase), {
      ...paginationSettings,
      filters,
    });
  };

  const columns = generateColumns({
    handleReassignProcess,
    isRemoteDataSource,
    currentFilters,
    domainLanguages,
  });
  return (
    <>
      <div className="history-toggle">
        <div>
          <DSSwitch
            uncheckedLabel="TEST BOT"
            checkedLabel="LIVE BOT"
            onChange={toggleDataSource}
            isChecked={isRemoteDataSource}
          />
          {!!Object.keys(urlQuery).length && (
            <FiltersArea
              analyticCase={analyticCase}
              filters={{
                intent: urlQuery['nlp.intent'],
                fromDate: urlQuery.fromDate,
                toDate: urlQuery.toDate,
              }}
              callback={getFilteredHistory}
            />
          )}
        </div>
        <Form name="export-corpus" onFinish={handleExport}>
          <Row>
            <Col>
              {isRemoteDataSource && isUserAbleToSeeThis('export-conversations:view') && (
                <Form.Item>
                  <Button
                    type="primary"
                    id="export-button"
                    htmlType="submit"
                    icon={<DownloadOutlined />}
                    className="flex align-middle uppercase"
                  >
                    {labels.btnExportMessage}
                  </Button>
                </Form.Item>
              )}
            </Col>
          </Row>
        </Form>
      </div>
      {isLoadingMessages && (
        <div style={{ marginLeft: '50%', marginBottom: '300px' }}>
          <Spin size="large" />
        </div>
      )}
      {!isLoadingMessages && (
        <Table
          locale={getCustomNoData()}
          columns={columns}
          loading={!messages}
          dataSource={messages}
          rowKey="id"
          onChange={onTableChange}
          pagination={{
            ...paginationWrapper,
            total,
          }}
        />
      )}
      <style jsx>{byMessageTableStyles}</style>
    </>
  );
};

export { ByMessageTable };
